<template>
    <div class="flex flex-col gap-2.5 pt-2.5">
        <!-- Search & add (needs the connections-integration flag; existing relationships always render) -->
        <div v-if="canAddRelationships" class="flex flex-col gap-2.5">
            <BaseInput
                v-model="searchInput"
                type="search"
                :aria-label="t('chat.pmPicker.searchLabel')"
                :placeholder="t('chat.pmPicker.searchLabel')"
                data-testid="picker-person-search">
                <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
            </BaseInput>
            <HorizontalDivider as="hr" spacing="none" />
            <div v-if="searchInput.trim()" class="flex flex-col gap-2.5 max-h-56 overflow-y-auto">
                <div v-if="isSearchPending && !searchResults.length" class="scaffold-body-small text-ink-muted px-1 py-2" data-testid="picker-person-searching">
                    {{ t("chat.pmPicker.searching") }}
                </div>
                <div v-else-if="!searchResults.length && onlyExistingMatches" class="scaffold-body-small text-ink-muted px-1 py-2" data-testid="picker-person-already-added">
                    {{ t("chat.pmPicker.alreadyAdded") }}
                </div>
                <div v-else-if="!searchResults.length" class="flex flex-col gap-2.5 px-1 py-2" data-testid="picker-person-add-new">
                    <p class="scaffold-body-small text-ink-muted">{{ t("chat.pmPicker.noResults") }}</p>
                    <div class="flex flex-col gap-2.5 sm:flex-row sm:items-center">
                        <BaseInput
                            v-model="addForm.name"
                            class="min-w-0 grow"
                            :aria-label="t('chat.pmPicker.nameLabel')"
                            :placeholder="t('chat.pmPicker.nameLabel')"
                            data-testid="picker-person-new-name" />
                        <BaseInput
                            v-model="addForm.jobTitle"
                            class="min-w-0 grow"
                            :aria-label="t('chat.pmPicker.titleLabel')"
                            :placeholder="t('chat.pmPicker.titleLabel')"
                            data-testid="picker-person-new-title" />
                        <BaseButton
                            theme="neutral"
                            size="sm"
                            class="w-28 shrink-0 justify-center sm:self-stretch"
                            :disabled="isAdding || !addForm.name.trim()"
                            data-testid="picker-person-add-button"
                            @click="addNewPerson">
                            {{ t("chat.pmPicker.add") }}
                        </BaseButton>
                    </div>
                </div>
                <button
                    v-for="connection in searchResults"
                    :key="connection.id"
                    type="button"
                    class="group flex items-center gap-2.5 p-1 rounded-md text-start transition-colors hover:bg-surface-hover"
                    :disabled="isAdding"
                    :data-testid="`picker-person-result-${connection.id}`"
                    @click="addPerson(connection)">
                    <PeopleAvatar size="xs" :name="connection.persona.name" :initials="firstInitial(connection.persona.name)" />
                    <span class="flex flex-col min-w-0 grow">
                        <span class="nadia-chat-semibold text-ink truncate">{{ connection.persona.name }}</span>
                        <span v-if="connection.job_title" class="scaffold-details-small text-ink-muted truncate">{{ connection.job_title }}</span>
                    </span>
                    <!-- Clicking the row registers them as a reviewee, which the name alone doesn't say. -->
                    <BaseChip size="2xs" class="shrink-0 min-w-16 justify-center transition-colors group-hover:bg-white" :data-testid="`picker-person-result-add-${connection.id}`">
                        {{ t("chat.pmPicker.add") }}
                    </BaseChip>
                </button>
            </div>
            <BaseNotification v-if="addError" theme="error" hide-dismiss :text="addError" />
        </div>

        <!-- Existing reviewees -->
        <div v-if="relationshipGroups.length" class="flex flex-col divide-y divide-border">
            <div v-for="group in relationshipGroups" :key="group.relationshipId" :data-testid="`picker-person-${group.relationshipId}`">
                <!-- Single task: the row carries that task's action. Multiple tasks: the row opens a step listing them. -->
                <div
                    v-if="group.reviews.length === 1"
                    class="flex items-center gap-3 rounded-md px-2.5 py-3"
                    :class="{ 'bg-surface-sunken': group.relationshipId === preselectedRelationshipId }">
                    <PeopleAvatar size="xs" :name="group.reviews[0].relationship?.reviewee_name" :initials="firstInitial(group.reviews[0].relationship?.reviewee_name)" />
                    <span class="min-w-0 grow flex flex-col">
                        <span class="nadia-chat-semibold text-ink truncate">{{ group.reviews[0].relationship?.reviewee_name }}</span>
                        <span v-if="group.reviews[0].relationship?.reviewee_job_title" class="scaffold-details-small text-ink-muted truncate">
                            {{ group.reviews[0].relationship?.reviewee_job_title }}
                        </span>
                    </span>
                    <ProgressIcon :status="groupProgress(group.reviews)" :label="getReviewGroupStatus(group.reviews)" />
                    <PickerTaskAction
                        :review="group.reviews[0]"
                        :current-conversation-hashid="currentConversationHashid"
                        :is-busy="isBusy"
                        :start-disabled="startDisabled"
                        @activate="emit('row-clicked', group.reviews[0])" />
                </div>
                <button
                    v-else
                    type="button"
                    class="group flex w-full items-center gap-3 rounded-md px-2.5 py-3 text-start"
                    :class="{ 'bg-surface-sunken': group.relationshipId === preselectedRelationshipId }"
                    @click="openPerson(group)">
                    <PeopleAvatar size="xs" :name="group.reviews[0].relationship?.reviewee_name" :initials="firstInitial(group.reviews[0].relationship?.reviewee_name)" />
                    <span class="min-w-0 grow flex flex-col">
                        <span class="nadia-chat-semibold text-ink truncate">{{ group.reviews[0].relationship?.reviewee_name }}</span>
                        <span v-if="group.reviews[0].relationship?.reviewee_job_title" class="scaffold-details-small text-ink-muted truncate">
                            {{ group.reviews[0].relationship?.reviewee_job_title }}
                        </span>
                    </span>
                    <ProgressIcon :status="groupProgress(group.reviews)" :label="getReviewGroupStatus(group.reviews)" />
                    <!-- Same width as an action button, so this row's icon lines up with the others. -->
                    <span class="ms-1 flex w-28 shrink-0 justify-end">
                        <i class="bi bi-chevron-right text-xs/none text-ink-muted transition-colors group-hover:text-ink" aria-hidden="true"></i>
                    </span>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import { useConnectionsSearch } from "~vue/Moments/composables/useConnectionsSearch";
import { getReviewGroupStatus, getReviewGroupStatusState } from "~vue/Moments/composables/useReviewGroupStatus";
import type { Connection } from "~vue/types/connection";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { sortBy, uniq } from "lodash";
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import PickerTaskAction from "./PickerTaskAction.vue";
import ProgressIcon, { type ProgressStatus } from "./ProgressIcon.vue";

interface Props {
    teamReviews: DecoratedPerformanceReview[];
    canAddRelationships: boolean;
    currentConversationHashid?: string;
    isBusy?: boolean;
    startDisabled?: boolean;
    preselectedRelationshipId?: string | null;
    /** Name the user gave Nadia for someone they don't review yet — seeds the search. */
    initialSearch?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
    currentConversationHashid: "",
    isBusy: false,
    startDisabled: false,
    preselectedRelationshipId: null,
    initialSearch: null,
});

const emit = defineEmits<{ "row-clicked": [review: DecoratedPerformanceReview]; "open-person": [relationshipId: string]; "person-added": [] }>();
const { t } = useI18n();

const firstInitial = (name: string | null | undefined): string => (name ?? "").trim().charAt(0).toUpperCase();

type RelationshipGroup = { relationshipId: string; reviews: DecoratedPerformanceReview[] };

const relationshipGroups = computed<RelationshipGroup[]>(() => {
    const withRelationships = props.teamReviews.filter((review) => !!review.relationship);
    const relationshipIds = uniq(sortBy(withRelationships, (review) => review.relationship?.reviewee_name).map((review) => String(review.relationship?.id)));
    return relationshipIds.map((relationshipId) => ({
        relationshipId,
        reviews: sortBy(
            withRelationships.filter((review) => String(review.relationship?.id) === relationshipId),
            (review) => review.cta?.id,
        ),
    }));
});

const openPerson = (group: RelationshipGroup) => {
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_GROUP_CLICKED, {
        relationship_id: group.relationshipId,
        is_expanded: true,
        review_count: group.reviews.length,
    });
    emit("open-person", group.relationshipId);
};

// --- Search & add ---

const searchInput = ref(props.canAddRelationships ? (props.initialSearch?.trim() ?? "") : "");
const { connections, searchQuery, isFetchingConnections, connectionsFetchError } = useConnectionsSearch();
searchQuery.value = searchInput.value;
const syncSearchQuery = useDebounceFn(() => {
    searchQuery.value = searchInput.value;
}, 500);
watch(searchInput, () => {
    syncSearchQuery();
});

const existingRevieweeIds = computed(() => new Set(props.teamReviews.map((review) => review.relationship?.reviewee_id).filter(Boolean)));
const searchResults = computed<Connection[]>(() => (connections.value ?? []).filter((connection) => !existingRevieweeIds.value.has(connection.id)));

// The search matched, but everyone it matched is already being reviewed (filtered out of
// searchResults) — point at the list below instead of offering to add a duplicate.
const onlyExistingMatches = computed(() => (connections.value ?? []).length > 0);

// Never offer the create-new form over a search that hasn't landed: `initialSearch` renders this
// panel before its first request resolves, so a fast click would create a second connection for
// someone the search was about to return. A failed search still opens it — an unknown answer
// shouldn't make adding anyone impossible.
const isSearchPending = computed(() => isFetchingConnections.value || (connections.value === null && !connectionsFetchError.value));

const addError = ref("");
const isAdding = ref(false);
const addPayload = ref<{ reviewee_id: string } | null>(null);
const { execute: executeAdd } = useFetch("/api/coach/performance_reviews/relationships/", { immediate: false }).post(addPayload).json();

// Register an existing connection as a reviewee in the current performance moment.
const registerReviewee = async (connectionId: string) => {
    addPayload.value = { reviewee_id: connectionId };
    await executeAdd(true);
};

// Both add paths end here, and both create a review relationship in the current moment —
// `is_new_connection` distinguishes registering someone who already existed from adding
// someone brand new.
const finishAdd = (connectionId: string, isNewConnection: boolean) => {
    searchInput.value = "";
    searchQuery.value = "";
    addForm.name = "";
    addForm.jobTitle = "";
    autoFilledName.value = "";
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_RELATIONSHIP_CREATED, {
        connection_id: connectionId,
        is_new_connection: isNewConnection,
        source: "chat_picker",
    });
    emit("person-added");
};

const addPerson = async (connection: Connection) => {
    if (isAdding.value) {
        return;
    }
    isAdding.value = true;
    addError.value = "";
    try {
        await registerReviewee(connection.id);
        finishAdd(connection.id, false);
    } catch {
        addError.value = t("chat.pmPicker.addError");
    } finally {
        isAdding.value = false;
    }
};

// --- Add someone new (no matching connection) ---

const addForm = reactive({ name: searchInput.value, jobTitle: "" });

// Mirror the search text into the name field while it still holds our last auto-fill,
// but stop the moment the user edits the name themselves (same pattern as AddPeopleDialog).
const autoFilledName = ref(searchInput.value);
watch(searchInput, () => {
    const query = searchInput.value.trim();
    if (!addForm.name || addForm.name === autoFilledName.value) {
        addForm.name = query;
        autoFilledName.value = query;
    }
});

const createPayload = ref<{ job_title: string; persona: { name: string } } | null>(null);
const { data: createdConnection, error: createConnectionError, execute: executeCreate } = useFetch("/api/connections/", { immediate: false }).post(createPayload).json();

const addNewPerson = async () => {
    if (isAdding.value || !addForm.name.trim()) {
        return;
    }
    isAdding.value = true;
    addError.value = "";
    try {
        createPayload.value = { job_title: addForm.jobTitle.trim(), persona: { name: addForm.name.trim() } };
        await executeCreate(true);
        const connectionId: string = createdConnection.value.id;
        logUserInteraction(AmplitudeEventNames.CONNECTION_ADDED, { source: "performance_review" });
        await registerReviewee(connectionId);
        finishAdd(connectionId, true);
    } catch {
        if (!isSessionEndedError(createConnectionError.value)) {
            addError.value = t("chat.pmPicker.addError");
        }
    } finally {
        isAdding.value = false;
    }
};

const GROUP_PROGRESS = { neutral: "not-started", active: "in-progress", done: "done" } as const satisfies Record<string, ProgressStatus>;
const groupProgress = (reviews: DecoratedPerformanceReview[]): ProgressStatus => GROUP_PROGRESS[getReviewGroupStatusState(reviews)];
</script>
